import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import logo from './assets/logo.webp';
import tal from './assets/takealot.svg';

const ALL_CATEGORIES = 'all';
const SELLER_BASE_URL = 'https://www.printwithpassiondecor.com/products';
const TAKEALOT_URL = 'https://www.takealot.com/all?qsearch=by%20wikus%20schalkwyk&sort=ReleaseDate%20Descending';

const CATEGORY_COLLECTIONS = [
  {
    id: 'all',
    label: 'All',
    categories: null,
  },
  {
    id: 'dining',
    label: 'Dining',
    categories: ['Table Runners', 'Placemat', 'Napkin', 'Tablecloths'],
  },
  {
    id: 'bedding',
    label: 'Bedding',
    categories: ['Fleece', 'Duvet Cover Sets', 'Toddler Fleece Blanket', 'Cot Set', 'Minky'],
  },
  {
    id: 'accents',
    label: 'Accents',
    categories: ['Desk Pad', 'Scatters', 'Rugs', 'Aprone'],
  },
];

function cleanTitle(title) {
  let cleanedTitle = String(title || '').replace(/\s*\([^)]*\)/g, '').trim();

  // Parse and use REACT_APP_STRIP_TEXTS to remove specified text patterns
  try {
    const stripTexts = JSON.parse(process.env.REACT_APP_STRIP_TEXTS || '[]');
    stripTexts.forEach(text => {
      cleanedTitle = cleanedTitle.replace(text, '').trim();
    });
  } catch (error) {
    console.error('Error parsing REACT_APP_STRIP_TEXTS:', error);
  }

  return cleanedTitle;
}

function stripHtml(html) {
  return String(html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
}

function displayCategoryName(category) {
  if (category === ALL_CATEGORIES) {
    return 'All categories';
  }
  if (category === 'Aprone') {
    return 'Aprons';
  }
  return category;
}

function getProductImage(product) {
  if (product?.product_img) {
    return `/product_images/${product.product_img}`;
  }
  return product?.images?.[0]?.src || '';
}

function getPrice(product) {
  return product?.variants?.[0]?.price || '0.00';
}

function getCompareAtPrice(product) {
  const variant = product?.variants?.[0];
  if (!variant?.compare_at_price) {
    return null;
  }

  return parseFloat(variant.compare_at_price) > parseFloat(variant.price)
      ? variant.compare_at_price
      : null;
}

function ProductCard({ product, imageFailed, onImageError }) {
  const title = cleanTitle(product.title);
  const description = stripHtml(product.body_html);
  const imageSrc = getProductImage(product);
  const compareAtPrice = getCompareAtPrice(product);
  const productUrl = `${SELLER_BASE_URL}/${product.handle}`;

  return (
      <article className="group overflow-hidden rounded-lg border border-[#e8ded4] bg-[#fffaf5] shadow-[0_10px_30px_rgba(45,31,22,0.08)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_44px_rgba(45,31,22,0.14)]">
        <a href={productUrl} className="block focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35" target="_blank" rel="noreferrer" aria-label={`View ${title} at Print with Passion Decor`}>
          <div className="relative aspect-square overflow-hidden bg-[#efe5dc]">
            {imageSrc && !imageFailed ? (
                <img
                    src={imageSrc}
                    onError={() => onImageError(product.id)}
                    alt={title}
                    loading="lazy"
                    className="h-full w-full object-cover object-center transition duration-300 group-hover:scale-105"
                />
            ) : (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm font-medium text-[#7d6b5f]">
                  Image unavailable
                </div>
            )}
            {product.product_type && (
                <span className="absolute left-3 top-3 rounded-md bg-[#6f442f] px-2.5 py-1 text-xs font-semibold text-[#fffaf5] shadow-sm">
                  {displayCategoryName(product.product_type)}
                </span>
            )}
          </div>
        </a>

        <div className="flex min-h-[222px] flex-col p-4">
          <h2 className="min-h-[56px] text-lg font-semibold leading-snug text-[#181412]">
            {title}
          </h2>

          <p className="mt-3 line-clamp-2 text-sm leading-6 text-[#6a5a50]">
            {description || 'A printed home accent from the DarkDataSkullz decor collection.'}
          </p>

          <div className="mt-auto flex items-end justify-between gap-4 pt-5">
            <div>
              <p className="text-lg font-bold text-[#181412]">R{getPrice(product)}</p>
              {compareAtPrice && (
                  <p className="text-sm text-[#6a5a50] line-through">R{compareAtPrice}</p>
              )}
            </div>
            <a
                href={productUrl}
                className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#8f4f2e] px-4 py-2 text-sm font-semibold text-[#fffaf5] transition duration-200 hover:bg-[#723d24] focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35 active:scale-[0.98]"
                target="_blank"
                rel="noreferrer"
            >
              View product
            </a>
          </div>
        </div>
      </article>
  );
}

function App() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollection, setSelectedCollection] = useState(ALL_CATEGORIES);
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);
  const [failedImages, setFailedImages] = useState({});

  // Read selected category from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('category');
    const collectionParam = params.get('collection');

    if (collectionParam && CATEGORY_COLLECTIONS.some(collection => collection.id === collectionParam)) {
      setSelectedCollection(collectionParam);
    }
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, []);

  // Update URL when category filter changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedCategory && selectedCategory !== 'all') {
      params.set('category', selectedCategory);
      params.delete('collection');
    } else if (selectedCollection && selectedCollection !== 'all') {
      params.set('collection', selectedCollection);
      params.delete('category');
    } else {
      params.delete('category');
      params.delete('collection');
    }
    const query = params.toString();
    window.history.replaceState(null, '', query ? `?${query}` : window.location.pathname);
  }, [selectedCategory, selectedCollection]);

  const loadProducts = useCallback(() => {
    setLoading(true);
    setError('');

    fetch('/products.json')
        .then(response => {
          if (!response.ok) {
            throw new Error(`Product feed returned ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          setProducts(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching products:', error);
          setError('We could not load the decor catalog. Please try again.');
          setLoading(false);
        });
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const categoryCounts = useMemo(() => products.reduce((counts, product) => {
    if (!product.product_type) {
      return counts;
    }

    counts[product.product_type] = (counts[product.product_type] || 0) + 1;
    return counts;
  }, {}), [products]);

  const uniqueCategories = useMemo(() => [
    ALL_CATEGORIES,
    ...Object.keys(categoryCounts).sort((a, b) => displayCategoryName(a).localeCompare(displayCategoryName(b))),
  ], [categoryCounts]);

  const activeCollection = CATEGORY_COLLECTIONS.find(collection => collection.id === selectedCollection) || CATEGORY_COLLECTIONS[0];

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return products
        .filter(product => {
          if (selectedCategory !== ALL_CATEGORIES) {
            return product.product_type === selectedCategory;
          }

          return !activeCollection.categories || activeCollection.categories.includes(product.product_type);
        })
        .filter(product => cleanTitle(product.title).toLowerCase().includes(normalizedSearch));
  }, [activeCollection, products, searchTerm, selectedCategory]);

  const heroProduct = products.find(product => product.product_type === 'Table Runners' && getProductImage(product)) || products.find(getProductImage);
  const activeFilterLabel = selectedCategory !== ALL_CATEGORIES
      ? displayCategoryName(selectedCategory)
      : activeCollection.label;

  const selectCollection = (collectionId) => {
    setSelectedCollection(collectionId);
    setSelectedCategory(ALL_CATEGORIES);
  };

  const selectCategory = (category) => {
    setSelectedCategory(category);
    if (category === ALL_CATEGORIES) {
      setSelectedCollection(ALL_CATEGORIES);
    } else {
      const matchingCollection = CATEGORY_COLLECTIONS.find(collection => collection.categories?.includes(category));
      setSelectedCollection(matchingCollection?.id || ALL_CATEGORIES);
    }
  };

  const handleImageError = (productId) => {
    setFailedImages(previous => ({
      ...previous,
      [productId]: true,
    }));
  };

  return (
      <div className="min-h-screen bg-[#f8f2eb] text-[#181412]">
        {/* Navigation Bar */}
        <header className="sticky top-0 z-50 bg-[#15110f] shadow-[0_10px_32px_rgba(32,22,16,0.24)]">
          <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center">
                <img src={logo} alt="DarkDataSkullz" className="h-9 w-auto max-w-full sm:h-10"/>
              </div>

              <div className="w-full sm:max-w-2xl">
                <div className="relative">
                  <label htmlFor="product-search" className="sr-only">Search products</label>
                  <input
                      id="product-search"
                      className="w-full rounded-md border border-[#dfd0c4] bg-[#fffaf5] px-4 py-2.5 pl-10 pr-10 text-[#181412] placeholder:text-[#6a5a50] focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35"
                      type="text"
                      placeholder="Search decor..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-5 w-5 text-[#6a5a50]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                  </div>
                  {searchTerm && (
                      <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#6a5a50] hover:text-[#181412] focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35"
                          onClick={() => setSearchTerm('')}
                          aria-label="Clear product search"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[#251914] text-[#fffaf5]">
          {heroProduct && (
              <img
                  src={getProductImage(heroProduct)}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover opacity-35"
              />
          )}
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(21,17,15,0.94),rgba(50,32,24,0.82),rgba(127,73,40,0.44))]" />
          <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
            <p className="mb-4 text-sm font-semibold text-[#e8c7a6]">DarkDataSkullz decor edit</p>
            <h1 className="font-display max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
              Home decor printed with character
            </h1>
            <p className="mt-5 max-w-[48ch] text-lg leading-8 text-[#f5e8dc] sm:text-xl">
              Layer dining rooms, bedrooms, and workspaces with richly printed pieces designed by Wikus Schalkwyk.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-[#f5e8dc]">Also available through</span>
              <a href={TAKEALOT_URL} className="inline-flex min-h-10 items-center rounded-md bg-[#fffaf5] px-3 py-2 transition hover:bg-[#efe5dc] focus:outline-none focus:ring-4 focus:ring-[#e8c7a6]/40" target="_blank" rel="noreferrer" aria-label="View this collection on Takealot">
                <img src={tal} alt="Takealot" className="h-7" />
              </a>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="border-b border-[#e8ded4] bg-[#fffaf5] shadow-sm">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2" aria-label="Featured category collections">
                {CATEGORY_COLLECTIONS.map(collection => (
                  <button
                      key={collection.id}
                      type="button"
                      onClick={() => selectCollection(collection.id)}
                      className={`min-h-10 rounded-full px-4 py-2 text-sm font-semibold transition duration-200 focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35 ${
                          selectedCategory === ALL_CATEGORIES && selectedCollection === collection.id
                              ? 'bg-[#6f442f] text-[#fffaf5]'
                              : 'bg-[#efe7de] text-[#4a382e] hover:bg-[#e3d4c6]'
                      }`}
                  >
                    {collection.label}
                  </button>
                ))}
              </div>

              <div className="grid gap-2 sm:grid-cols-[minmax(220px,320px)_auto] sm:items-center">
                <label htmlFor="category-select" className="sr-only">Choose a product category</label>
                <select
                    id="category-select"
                    className="min-h-11 rounded-md border border-[#dfd0c4] bg-[#fffdf9] px-3 text-sm font-medium text-[#2b211c] focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35"
                    value={selectedCategory}
                    onChange={(event) => selectCategory(event.target.value)}
                >
                  {uniqueCategories.map(category => (
                      <option key={category} value={category}>
                        {displayCategoryName(category)}
                        {category !== ALL_CATEGORIES ? ` (${categoryCounts[category] || 0})` : ''}
                      </option>
                  ))}
                </select>
                <p className="text-sm text-[#6a5a50]" aria-live="polite">
                  {loading ? 'Loading catalog...' : `${filteredProducts.length} of ${products.length} pieces`}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-[#8f4f2e]">Browsing {activeFilterLabel}</p>
              <h2 className="font-display text-3xl font-semibold text-[#181412]">Decor catalog</h2>
            </div>
            {(searchTerm || selectedCategory !== ALL_CATEGORIES || selectedCollection !== ALL_CATEGORIES) && (
                <button
                    type="button"
                    className="self-start rounded-md border border-[#d7c6b7] px-3 py-2 text-sm font-semibold text-[#4a382e] transition hover:bg-[#efe7de] focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35 sm:self-auto"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory(ALL_CATEGORIES);
                      setSelectedCollection(ALL_CATEGORIES);
                    }}
                >
                  Reset browsing
                </button>
            )}
          </div>

          {loading ? (
              <div className="flex h-64 flex-col items-center justify-center gap-4 text-[#6a5a50]" role="status" aria-live="polite">
                <div className="flex gap-2" aria-hidden="true">
                  <span className="h-3 w-3 animate-pulse rounded-full bg-[#8f4f2e]"></span>
                  <span className="h-3 w-3 animate-pulse rounded-full bg-[#b97849] [animation-delay:120ms]"></span>
                  <span className="h-3 w-3 animate-pulse rounded-full bg-[#d2a06f] [animation-delay:240ms]"></span>
                </div>
                <p className="font-medium">Loading the decor catalog...</p>
              </div>
          ) : error ? (
              <div className="mx-auto max-w-xl rounded-lg border border-[#e5c7ad] bg-[#fffaf5] p-8 text-center shadow-[0_10px_30px_rgba(45,31,22,0.08)]">
                <svg className="mx-auto h-12 w-12 text-[#8f4f2e]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v4m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
                </svg>
                <h2 className="mt-3 font-display text-2xl font-semibold text-[#181412]">Catalog did not load</h2>
                <p className="mt-2 text-[#6a5a50]">{error}</p>
                <button
                    type="button"
                    onClick={loadProducts}
                    className="mt-5 inline-flex min-h-11 items-center justify-center rounded-md bg-[#8f4f2e] px-5 py-2 text-sm font-semibold text-[#fffaf5] transition hover:bg-[#723d24] focus:outline-none focus:ring-4 focus:ring-[#c7834a]/35"
                >
                  Try again
                </button>
              </div>
          ) : filteredProducts.length === 0 ? (
              <div className="text-center text-[#6a5a50] py-20">
                <svg className="mx-auto h-12 w-12 text-[#6a5a50]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="mt-2 text-xl font-semibold text-[#181412]">No products found</h2>
                <p className="mt-1">Try another search term or reset the current filters.</p>
              </div>
          ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredProducts.map(product => (
                    <ProductCard
                        key={product.id}
                        product={product}
                        imageFailed={Boolean(failedImages[product.id])}
                        onImageError={handleImageError}
                    />
                ))}
              </div>
          )}
        </main>
        <footer className="mt-8 border-t border-[#e8ded4] bg-[#f2e8de]">
          <div className="mx-auto grid max-w-7xl gap-3 px-4 py-8 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:px-8">
            <h2 className="text-lg font-semibold text-[#4a382e]">Seller note</h2>
            <div className="max-w-[52ch] space-y-2 text-sm leading-6 text-[#6a5a50]">
                <p>
                  The products, prices, and availability displayed on this website are provided for informational
                  and promotional purposes only. We strive to keep all information accurate and up to date;
                  however, prices and product details may change without notice.
                </p>
                <p>
                  This site does not sell any products directly. Clicking on a product will redirect you
                  to the official seller's website, where you can review the most current information.
                </p>
                <p className="text-xs italic text-[#6a5a50]">
                  We may earn a small commission through affiliate links at no additional cost to you.
                </p>
            </div>
          </div>
        </footer>
      </div>
  );
}

export default App;
